import { useTranslation } from 'react-i18next';
import { KoobTooltip, KoobButton, sanitizeRichText } from '@koob/ui';
import { requestConnection } from 'api/requestConnection';
import ExperienceType from '../../constants/ExperienceType';
import { useApp, useError, useExperienceBooking, useSnack } from '../../hooks';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ExperienceTypeIcon from '../../containers/Experiences/Partials/ExperienceTypeIcon';
import { formatCurrency, tripFactsheetUrl } from 'utils';
import ExperienceGuaranteedDepartureIcon from './ExperienceGuaranteedDepartureIcon';
import { Lightning } from '../Lightning';
import {
  ExperienceCurrencyMismatch,
  ExperiencePricingTypeEnum,
  ProductTypeEnum,
} from '@koob/enums';
import ExperienceNotAvailableDueToCurrencyMismatch from '@koob/experiences/src/components/ExperienceNotAvailableDueToCurrencyMismatch';
import {
  useTripStore,
  useDays,
  useTripFilters,
  useSelectedType,
} from '@koob/trip-designer';
import { EXPERIENCES_CATALOG_SCROLL_KEY } from 'constants';

// Compose-mode state shared by the card button and the map popup button.
export const useExperienceSelection = (experience, mode) => {
  const tripFilters = useTripFilters();
  const days = useDays();
  const experiencesMap = useTripStore(state => state.experiences);

  const isSelectedExperienceInTrip =
    mode === 'compose' &&
    Boolean(
      days?.some(day => {
        const dayExperience = day?.experienceId
          ? experiencesMap?.[day.experienceId]
          : null;
        return (
          dayExperience?.id === experience?.id && !dayExperience?.isRepeatable
        );
      }),
    );

  // Same pax fallback chain as useGlobalTripPrice; an unknown count (0) never blocks.
  const composition = tripFilters?.composition;
  const tripTravelersCount =
    composition?.count ??
    (composition?.adults ?? tripFilters?.adultPax ?? 0) +
      (composition?.children ??
        composition?.childrenBirthdates?.length ??
        tripFilters?.childrenBirthdates?.length ??
        0);
  const isBelowMinPax =
    mode === 'compose' &&
    tripTravelersCount > 0 &&
    experience?.minPax > tripTravelersCount;

  return { isSelectedExperienceInTrip, isBelowMinPax };
};

export const ExperienceCard = ({
  experience,
  refreshExperiences,
  small,
  mode = 'list',
  onSelect,
  hasExperienceSummary,
  compatibility,
  isForTrip,
  source,
  locale,
}) => {
  const { t } = useTranslation('experiences');
  const navigate = useNavigate();
  const { sendErrorSnack } = useError();
  const { notify } = useSnack();
  const { currentUserRole } = useApp();
  const tripFilters = useTripFilters();
  const selectedType = useSelectedType();
  const { experienceFilters: filters, setExperienceFilters: setFilters } =
    useExperienceBooking();

  const { isSelectedExperienceInTrip, isBelowMinPax } = useExperienceSelection(
    experience,
    mode,
  );

  const experienceName = experience?.name?.toLowerCase().replace(/\s+/g, '-');

  const handleCardClick = () => {
    // A showcased card has no detail page for this TO: it asks for access
    // from the card (KD-3873).
    if (mode !== 'list' || experience?.showcased) return;
    saveCatalogScroll();
    const params = new URLSearchParams();
    if (locale) {
      params.set('locale', locale);
    }
    const queryString = params.toString();
    navigate(`${experience?.id}${queryString ? `?${queryString}` : ''}`);
  };

  const handleRequest = async e => {
    try {
      e.preventDefault();
      e.stopPropagation();
      if (currentUserRole === 'to_user') {
        notify(t('requestConnectionNoRight'), { type: 'error' });
        return;
      }
      await requestConnection(experience?.organizationId, 'experience');
      if (refreshExperiences) {
        refreshExperiences();
      }
      notify(t('requestConnectionSuccess'));
    } catch (error) {
      sendErrorSnack(error);
    }
  };

  // Persist the catalog scroll position when leaving for the factsheet so it
  // can be restored on return (only relevant in the marketplace list).
  const saveCatalogScroll = () => {
    if (mode === 'list') {
      sessionStorage.setItem(
        EXPERIENCES_CATALOG_SCROLL_KEY,
        String(window.scrollY),
      );
    }
  };

  const isFromPartnership =
    isForTrip &&
    experience.organizationId?.toString() !== tripFilters.dmcId?.toString();

  const hasCurrencyMismatch =
    experience?.price?.currency === ExperienceCurrencyMismatch;

  return (
    <>
      <div
        className={`relative isolate bg-white rounded-2xl ${
          source ? '' : 'border border-solid border-k-gray'
        } overflow-hidden h-full flex flex-col ${
          mode === 'list' && !experience?.showcased ? 'cursor-pointer' : ''
        }`}
        onClick={handleCardClick}
      >
        <div
          className={
            small ? 'flex flex-col 2xl:flex-row' : 'flex flex-1 flex-col'
          }
        >
          <div
            className={small ? 'relative 2xl:w-52 2xl:flex-none' : 'relative'}
          >
            {!experience?.connectionRequestId &&
              !['trip-export', 'itinerary'].includes(mode) && (
                <div
                  className={`absolute z-10 cursor-pointer backdrop-blur-sm bg-orange-500 bg-opacity-70 w-full p-3 rounded-t-2xl ${
                    small ? '2xl:rounded-tr-none' : ''
                  }`}
                  onClick={handleRequest}
                >
                  <div className="flex justify-center items-center space-x-1 text-white">
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                      />
                    </svg>
                    <div>{t('requestConnection')}</div>
                  </div>
                </div>
              )}

            <div className="absolute top-4 right-4 flex gap-2">
              {experience?.price?.instantAvailability && (
                <Lightning popover={t('instantAvailability')} />
              )}
              {experience.pricingType !== ExperiencePricingTypeEnum.FIT && (
                <ExperienceGuaranteedDepartureIcon absolute={false} />
              )}
            </div>

            {experience?.tags?.length > 0 && (
              <div
                className={`absolute bottom-0 backdrop-blur-sm bg-purple-600 bg-opacity-50 w-full p-2 ${
                  small ? '2xl:rounded-bl-2xl' : ''
                }`}
              >
                <div className="text-center text-white text-xs">
                  {experience?.tags?.map((style, index) => (
                    <span key={experience?.id + index}>
                      {style}
                      {index !== experience.tags.length - 1 && ', '}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <img
              className={[
                'w-full object-cover bg-gray-100',
                'rounded-t-2xl',
                small ? '2xl:rounded-tr-none 2xl:rounded-bl-2xl' : '',
                small ? 'h-44 2xl:h-full 2xl:min-h-44' : 'h-56',
              ].join(' ')}
              src={
                experience?.imageUrl ??
                experience?.pictures?.[0]?.imageUrl ??
                '/img/hotel-image-placeholder.png'
              }
              onError={ev =>
                (ev.target.src = '/img/hotel-image-placeholder.png')
              }
              alt=""
            />
          </div>

          <div
            className={[
              small ? 'flex items-center px-4 py-3' : 'p-4',
              'flex-1 flex flex-col',
            ].join(' ')}
          >
            <div className="flex flex-col space-y-2 flex-1">
              <div className="flex justify-between items-center">
                <div className="flex space-x-1.5 items-center">
                  {experience?.isTransitionalProgram && (
                    <KoobTooltip
                      tip={t('transitionalProgram')}
                      position="right"
                    >
                      <div className="flex justify-center items-center h-8 w-8 bg-k-orange rounded-full">
                        <i className="far fa-exchange-alt text-white" />
                      </div>
                    </KoobTooltip>
                  )}

                  {isForTrip ? (
                    <a
                      href={tripFactsheetUrl(`/experiences/${experience?.id}`, {
                        locale,
                        tripId: tripFilters?.tripId,
                      })}
                      target="_blank"
                      className={'underline hover:text-blue-700'}
                      rel="noreferrer"
                    >
                      <div className="font-medium">{experience?.name}</div>
                    </a>
                  ) : (
                    <div className="font-medium">{experience?.name}</div>
                  )}
                </div>

                {mode !== 'trip-export' && !isForTrip && (
                  <ExperienceTypeIcon
                    sizeClass="w-6 h-6"
                    type={experience?.experienceType}
                    config={experience?.transferType}
                  />
                )}
              </div>

              <div className="flex justify-between items-center">
                <div className="flex space-x-3 flex-wrap text-gray-500 text-sm">
                  <div className="flex gap-1.5 items-center">
                    <i className="far fa-clock text-lg" />

                    <div>
                      {experience?.durationDays === 0 &&
                      experience?.durationHours === 0 &&
                      experience?.durationMinutes === 0
                        ? t('durationUnitOptions.days', {
                            count: experience?.durationDays,
                          })
                        : null}{' '}
                      {experience?.durationDays > 0
                        ? t('durationUnitOptions.days', {
                            count: experience?.durationDays,
                          })
                        : null}{' '}
                      {experience?.durationHours > 0
                        ? t('durationUnitOptions.hours', {
                            count: experience?.durationHours,
                          })
                        : null}{' '}
                      {experience?.durationMinutes > 0
                        ? t('durationUnitOptions.minutes', {
                            count: experience?.durationMinutes,
                          })
                        : null}{' '}
                    </div>
                  </div>

                  <div className="flex space-x-1.5 items-center">
                    <i className="far fa-flag text-lg" />
                    <div>{experience?.startPosition?.name}</div>
                  </div>

                  <div className="flex space-x-1.5 items-center">
                    <i className="far fa-flag-checkered text-lg" />
                    <div>{experience?.endPosition?.name}</div>
                  </div>

                  {mode === 'compose' && experience?.isRepeatable && (
                    <div className="flex space-x-1.5 items-center">
                      <KoobTooltip tip={t('repeatable')} position="left">
                        <i className="far fa-clone text-lg text-k-orange" />
                      </KoobTooltip>
                    </div>
                  )}
                </div>
              </div>

              {experience?.minPax > 1 && (
                <div className="text-red-600 text-sm font-medium">
                  {t('minimumTravelers', { count: experience?.minPax })}
                </div>
              )}

              {experience?.price?.price !== undefined &&
                !hasCurrencyMismatch && (
                  <div className="flex justify-end items-center space-x-3">
                    {filters?.searchMinPrice && (
                      <div className="uppercase text-xs text-gray-500">
                        {t('priceFrom')}
                      </div>
                    )}

                    <div className="text-orange-600 text-lg font-medium">
                      {formatCurrency({
                        amount: experience?.price?.price,
                        currency: experience?.price?.currency,
                      })}
                    </div>
                  </div>
                )}

              {hasCurrencyMismatch && (
                <ExperienceNotAvailableDueToCurrencyMismatch />
              )}

              <div className="flex justify-end gap-1">
                {ExperienceType.isProgram(experience?.experienceType) &&
                  experience?.summary &&
                  hasExperienceSummary && (
                    <div
                      className="col-span-2 prose prose-sm"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(experience?.summary),
                      }}
                    />
                  )}
              </div>

              {/* Spacer pushes the provider + CTA row to the bottom so it
                  sits at the same height across cards of varying content
                  height (e.g. 1- vs 2-line titles). A flex-1 grower is used
                  instead of mt-auto because the parent's space-y-* utility
                  has higher specificity and would override margin-top. */}
              <div className="flex-1" />

              <div
                className={[
                  'z-10 mt-3 grid gap-3 items-center',
                  'grid-cols-[minmax(0,1fr)_minmax(max-content,1fr)]',
                ].join(' ')}
              >
                <div
                  className={[
                    'flex items-center space-x-1.5 text-gray-500',
                    mode === 'itinerary' && 'col-span-2',
                  ].join(' ')}
                >
                  {isFromPartnership && (
                    <KoobTooltip tip={t('partnership')} position="top">
                      <div className="flex justify-center items-center h-8 w-8 bg-k-orange rounded-md">
                        <i className="fas fa-handshake text-white" />
                      </div>
                    </KoobTooltip>
                  )}

                  <img
                    className="w-8 h-8 object-cover bg-gray-100 rounded-md"
                    src={
                      experience?.organizationImages ??
                      '/img/hotel-image-placeholder.png'
                    }
                    onError={ev =>
                      (ev.target.src = '/img/hotel-image-placeholder.png')
                    }
                    alt=""
                  />

                  <div className="min-w-0">
                    <div className="text-2xs uppercase font-medium">
                      {t('providedBy')}
                    </div>
                    <div className="w-full text-xs text-right font-medium truncate leading-tight">
                      {experience?.organizationName}
                    </div>
                  </div>
                </div>

                <div>
                  {/* A showcased card asks for access instead (KD-3873). */}
                  {mode === 'list' &&
                    !hasCurrencyMismatch &&
                    !experience?.showcased && (
                      <KoobButton
                        block
                        variant="primary"
                        data-testid={`${experienceName}-availability`}
                      >
                        <div className="text-sm px-1 whitespace-nowrap">
                          {t('seeExperience')}
                        </div>
                      </KoobButton>
                    )}

                  {mode === 'compose' && (
                    <div>
                      {compatibility !== 'incompatible' ? (
                        <KoobButton
                          onClick={e => {
                            e.stopPropagation();
                            if (experience?.requested) {
                              onSelect(experience);
                            } else {
                              notify(t('requestConnectionToAdd'), {
                                type: 'error',
                              });
                            }
                          }}
                          variant={
                            compatibility === 'bestMatch'
                              ? 'success'
                              : 'primary'
                          }
                          disabled={
                            isSelectedExperienceInTrip ||
                            isBelowMinPax ||
                            selectedType !== ProductTypeEnum.EXPERIENCE
                          }
                          data-testid={`btn-${experienceName}`}
                          block
                        >
                          <div className="flex justify-center space-x-1.5 items-center">
                            {compatibility === 'bestMatch' && (
                              <svg
                                className="h-5 w-5"
                                height="1em"
                                viewBox="0 0 512 512"
                              >
                                <path d="M243.5 37.3c8-3.4 17-3.4 25 0l176.7 75c11.3 4.8 18.9 15.5 18.8 27.6c-.5 94-39.4 259.8-195.5 334.5c-7.9 3.8-17.2 3.8-25.1 0C87.3 399.6 48.5 233.8 48 139.8c-.1-12.1 7.5-22.8 18.8-27.6l176.7-75zM281 7.8c-16-6.8-34-6.8-50 0L54.3 82.8c-22 9.3-38.4 31-38.3 57.2c.5 99.2 41.3 280.7 213.6 363.2c16.7 8 36.1 8 52.8 0C454.7 420.7 495.5 239.2 496 140c.1-26.2-16.3-47.9-38.3-57.2L281 7.8zm82.3 195.5c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L224 297.4l-52.7-52.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l64 64c6.2 6.2 16.4 6.2 22.6 0l128-128z" />
                              </svg>
                            )}
                            <div className="text-sm font-medium whitespace-nowrap">
                              {t(
                                selectedType === ProductTypeEnum.EXPERIENCE
                                  ? 'misc:select'
                                  : 'tripDesigner:compose.selectDay',
                              )}
                            </div>
                          </div>
                        </KoobButton>
                      ) : (
                        <div className="w-full text-red-600 h-9 bg-gray-200 cursor-not-allowed px-4 rounded flex space-x-1.5 justify-center items-center">
                          <svg
                            className="h-5 w-5"
                            height="1em"
                            viewBox="0 0 512 512"
                          >
                            <path d="M243.5 37.3c8-3.4 17-3.4 25 0l176.7 75c11.3 4.8 18.9 15.5 18.8 27.6c-.5 94-39.4 259.8-195.5 334.5c-7.9 3.8-17.2 3.8-25.1 0C87.3 399.6 48.5 233.8 48 139.8c-.1-12.1 7.5-22.8 18.8-27.6l176.7-75zM281 7.8c-16-6.8-34-6.8-50 0L54.3 82.8c-22 9.3-38.4 31-38.3 57.2c.5 99.2 41.3 280.7 213.6 363.2c16.7 8 36.1 8 52.8 0C454.7 420.7 495.5 239.2 496 140c.1-26.2-16.3-47.9-38.3-57.2L281 7.8zM180.7 180.7c-6.2 6.2-6.2 16.4 0 22.6L233.4 256l-52.7 52.7c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L256 278.6l52.7 52.7c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L278.6 256l52.7-52.7c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L256 233.4l-52.7-52.7c-6.2-6.2-16.4-6.2-22.6 0z" />
                          </svg>
                          <span className="font-medium text-sm">
                            {t('incompatible')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExperienceCard;
